import { FilterOptionType } from './RosterPlan.types';

const getUniqueValue = (arr: FilterOptionType[]) => {
  if (!arr.length) return [];
  return Array.from(new Set(arr.map((value) => value.label))).map(
    (label) => arr.find((value) => value.label === label)!
  );
};

const isStringOrNumber = (value: unknown): value is string | number =>
  typeof value === 'string' || typeof value === 'number';

const toFilterOption = (
  label: unknown,
  id: unknown
): FilterOptionType | null => {
  const stringLabel = (label ?? '').toString().trim();
  if (!stringLabel) return null;
  if (!isStringOrNumber(id)) return null;
  return { label: stringLabel, id };
};

export const getUniqueFilterValues = ({
  data,
  labelKey,
  idKey
}: {
  data?: any[];
  labelKey: string;
  idKey?: string;
}): FilterOptionType[] => {
  if (!data) return [];

  if (labelKey.includes('.') && idKey) {
    const [parentLabelKey, childLabelKey] = labelKey.split('.');
    const [parentIdKey, childIdKey] = idKey.split('.');
    const values = data
      .map((employee) => {
        const parentLabel = employee[parentLabelKey as keyof typeof data];
        const parentId = employee[parentIdKey as keyof typeof data];
        return toFilterOption(
          parentLabel?.[childLabelKey],
          parentId?.[childIdKey]
        );
      })
      .filter((value): value is FilterOptionType => value !== null)
      .sort((a, b) => a.label.localeCompare(b.label));

    return getUniqueValue(values);
  }

  if (Array.isArray(data[0]?.[labelKey as keyof (typeof data)[0]])) {
    const values = data
      .flatMap((employee, index) => {
        const arr = employee[labelKey as keyof typeof data] as unknown[];
        return (arr ?? []).map((item, i) =>
          toFilterOption(item, `${item}-${index}-${i}`)
        );
      })
      .filter((value): value is FilterOptionType => value !== null);

    const uniqueValues = Array.from(
      new Set(values.map((value) => value.label))
    ).map((label) => values.find((value) => value.label === label)!);

    return uniqueValues;
  }

  if (labelKey && idKey) {
    const values = data
      .map((employee) =>
        toFilterOption(
          employee[labelKey as keyof typeof employee],
          employee[idKey as keyof typeof employee]
        )
      )
      .filter((value): value is FilterOptionType => value !== null)
      .sort((a, b) => a.label.localeCompare(b.label));

    return getUniqueValue(values);
  }

  return [];
};

export const normalizeToOptionArray = (
  value: FilterOptionType | FilterOptionType[] | null | undefined
): FilterOptionType[] => (Array.isArray(value) ? value : value ? [value] : []);

export const anyOptionLabelEquals = (
  options: FilterOptionType[],
  targetLabel: string | undefined
): boolean =>
  !!targetLabel && options.some((option) => option.label === targetLabel);

export const anyOptionIdEqualsAsString = (
  options: FilterOptionType[],
  targetId: string | number | undefined
): boolean =>
  targetId !== undefined &&
  options.some((option) => String(option.id) === String(targetId));

export const buildLabelSet = (options: FilterOptionType[]) =>
  new Set(options.map((option) => option.label));
